import React, { useEffect, useRef, useState } from "react";
import {
  FaRegClone,
  FaChevronDown,
  FaChevronUp,
  FaPlus,
  FaMinus,
} from "react-icons/fa";
import { GiCheckMark } from "react-icons/gi";
import { MdCheck } from "react-icons/md";
import { Trash2, Pencil, ArrowRightLeft, Check } from "lucide-react";
import ValidationPreview from "./ValidationPreview";
import ViewValidationModal from "./ViewValidationModal";
import FieldModal from "../../components/workflow/FieldModal";
import FieldInfoIcon from "../../components/common/FieldInfoIcon";
import ReactSelector from "../../components/common/CommonReactSelector";
import ActionIconButton from "../../components/common/ActionIconButton";
import translations from "../../components/common/Translations";
import { fieldNameForUniqueKey } from "../../utils/workflowVisibilityRules";
import { isRTL } from "../../utils/rtl";
import {
  formatLinkedWorkflowRuleSummary,
  getAdvancedLinkedWorkflowRules,
  getLinkedWorkflowIdForOption,
  isLinkableFieldType,
} from "../../utils/workflowLinkedWorkflowRules";

// Plain titled card -- groups a handful of related controls so no single
// card in the preview panel ends up holding everything at once.
const Section = ({ title, right, children, className = "" }) => (
  <div className={`rounded-lg border border-slate-200 bg-white p-3 ${className}`}>
    <div className="mb-2 flex items-center justify-between gap-2">
      <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-500">
        {title}
      </p>
      {right}
    </div>
    {children}
  </div>
);

// Same card, but collapsed by default -- for the less-frequently-needed
// configuration (visibility rules, option management, photo constraints)
// so the panel isn't a wall of controls for every field type.
const CollapsibleSection = ({ title, defaultOpen = false, badge, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center justify-between p-3 text-left"
      >
        <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.06em] text-slate-500">
          {title}
          {badge}
        </span>
        {open ? (
          <FaChevronUp className="h-3 w-3 shrink-0 text-slate-400" />
        ) : (
          <FaChevronDown className="h-3 w-3 shrink-0 text-slate-400" />
        )}
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
};

const FieldDetails = ({
  droppedItems, setDroppedItems,
  dragItems, setDragItems,
  setPreview,
  preview,
  theme,
  isEdit,
  isClone,
  langMode,
  isPageWise,
  totalPages,
  onMoveFieldToPage,
  handleCloneElement,
  handleRemoveElement,
  handleInputChange,
  handleToggleChange,
  pdfMandatory,
  handlePdfInput,
  addPdffield,
  pdfInput,
  showInput,
  reportMandatory,
  reportField,
  reportInput,
  handleReportInput,
  addReportfield,
  Translations,
  shouldRenderSelectOption,
  groupedRadioButtons,
  collapse,
  collapsedGroups,
  toggleCollapse,
  selectedRadioId,
  handleRadioBtnBelongs,
  setSelectedRadioId,
  shouldRenderSelectOptionEdit,
  isEditShow,
  setIsEditShow,
  radioButtons,
  tempEditValues,
  setTempEditValues,
  handleRadioOptions,
  handleRadioDeleteOptions,
  isNewFieldDragged,
  displaySelectedValue,
  question,
  inputValues,
  tempInputValue,
  setTempInputValue,
  handleAddInput,
  handleRemoveInput,
  toast,
  setCurrentValidation,
  currentValidation,
  setValidationModal,
  setValidationId,
  validationId,
  validationFormValues,
  setIsChecked,
  getHeadline,
  reporticon,
  setReportIcon,
  pdficon,
  setPdfIcon,
  setValidationFormValues,
  setCompareValidationModal,
  updateValidationField,
  handleValidationDelete,
  setPdfInput,
  setreportInput,
  handleOptionShowToggle,
  handleAllowGalleryToggle,
  handlePhotoConstraintChange,
  aspectRatios = [],
  linkedWorkflowOptions = [],
  linkedWorkflowsLoading = false,
  onLinkedWorkflowChange,
}) => {
  const isRtl = isRTL(langMode);
  const [viewValidationModal, setViewValidationModal] = useState(false);
  const [open, setOpen] = useState(false);
  // "Move to Page" popover -- same check-mark-on-selected list style already
  // used by SearchFieldPicker.js and the page "..." options menu in
  // DynamicWorkFlow.js, instead of a plain <select> styled to look like a
  // pill (which read as an oversized, inconsistent control next to the
  // Clone/Edit/Delete icon buttons).
  const [isMovePageOpen, setIsMovePageOpen] = useState(false);
  const movePageRef = useRef(null);

  useEffect(() => {
    if (!isMovePageOpen) return undefined;
    const onDocDown = (e) => {
      if (movePageRef.current?.contains(e.target)) return;
      setIsMovePageOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [isMovePageOpen]);

  const optionInputKeyFor = (input) =>
    input.radioKey != null && input.radioKey !== ""
      ? `input-${input.radioKey}`
      : `input-${input.id}`;

  const selectedParentOptionRows = [];
  (preview.visibilityRules || []).forEach((rule, ruleIndex) => {
    (rule.conditions || []).forEach((c, condIndex) => {
      selectedParentOptionRows.push({
        key: `vr-${ruleIndex}-${condIndex}-${c.sourceUniqueKey}-${c.expectedValue}`,
        que: fieldNameForUniqueKey(droppedItems, c.sourceUniqueKey),
        option: c.expectedValue,
      });
    });
  });

  const visibilityRuleGroups =
    preview.visibilityRules?.length > 0
      ? preview.visibilityRules
      : [{ conditions: [] }];
  const selectableVisibilityGroups = Object.entries(groupedRadioButtons || {}).filter(
    ([, inputs]) =>
      Array.isArray(inputs) &&
      inputs.some((input) => input.uniqueKey !== preview.uniqueKey)
  );

  // A workflow can only be linked from an option, so the picker is offered per
  // confirmed option of a radio / checkbox / dropdown field. `allInputValues` is
  // the same list the rule's `expectedValue` matches on.
  const canLinkWorkflow = isLinkableFieldType(preview.fieldType);
  const linkableOptions = Array.isArray(preview.allInputValues)
    ? preview.allInputValues.filter((input) => input.inputValue !== "")
    : [];
  const linkedWorkflowNameById = linkedWorkflowOptions.reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {});
  // Rules the per-option picker can't represent (extra ANDed conditions, or a
  // condition on another field). Shown read-only because save echoes them back.
  const advancedLinkedRules = getAdvancedLinkedWorkflowRules(preview);
  const linkedWorkflowCount =
    linkableOptions.filter(
      (input) => getLinkedWorkflowIdForOption(preview, input.inputValue) != null
    ).length + advancedLinkedRules.length;

  return (
    <div
      className={`block w-full rounded-lg border border-slate-200 bg-white p-3 text-[13px] leading-[1.45] shadow-sm transition-all duration-200 ${theme === "dark"
        ? "dark-mode "
        : theme === "high-contrast"
          ? "high-contrast"
          : ""
        }`}
    >
      <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-3">
        <div className="min-w-0">
          <h6 className="text-[13.5px] font-bold text-slate-800">
            {Translations[langMode].preview}
          </h6>
          <p className="mt-0.5 max-w-[190px] truncate text-[11px] font-medium text-slate-400">
            {preview.fieldName}
          </p>
        </div>
        <div className="flex items-center justify-end gap-2">
          {/* Only shown when there's actually more than one page to move to --
              previously the only way to get a field onto another page was to
              drop a new one there, or duplicate the whole page; there was no
              way to relocate a field that already existed. Same compact
              icon-button + check-mark-list popover style as SearchFieldPicker.js
              and the page "..." options menu, so it sits naturally alongside
              the Clone/Edit/Delete icons instead of standing out as an
              oversized pill. */}
          {isPageWise && Number(totalPages) > 1 && (
            <div className="relative shrink-0" ref={movePageRef}>
              <ActionIconButton
                onClick={() => setIsMovePageOpen((o) => !o)}
                tone="gray"
                className={`!h-8 !w-8 !rounded-lg !border !border-slate-200 !bg-white shadow-sm hover:!bg-gray-50 ${
                  isMovePageOpen ? "!bg-gray-50" : ""
                }`}
                ariaLabel={translations[langMode]?.moveToPage ?? "Move to Page"}
                tooltipText={translations[langMode]?.moveToPage ?? "Move to Page"}
                tooltipPosition="top"
              >
                <ArrowRightLeft size={14} />
              </ActionIconButton>
              {isMovePageOpen && (
                <div
                  className={`absolute top-full z-40 mt-1.5 w-40 rounded-xl border border-slate-100 bg-white py-1.5 shadow-xl ${
                    isRtl ? "left-0" : "right-0"
                  }`}
                >
                  <p className="px-3.5 pb-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-slate-400">
                    {translations[langMode]?.moveToPage ?? "Move to Page"}
                  </p>
                  {Array.from({ length: Number(totalPages) }, (_, i) => i + 1).map((p) => {
                    const isCurrent = Number(preview.page ?? 1) === p;
                    return (
                      <button
                        key={p}
                        type="button"
                        disabled={isCurrent}
                        onClick={() => {
                          onMoveFieldToPage?.(preview.uniqueKey, p);
                          setIsMovePageOpen(false);
                        }}
                        className={`flex w-full items-center justify-between gap-2 px-3.5 py-2 text-sm text-left ${
                          isCurrent
                            ? "cursor-default bg-blue-50/60 font-semibold text-[#236DB4]"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        {`${translations[langMode]?.Page ?? "Page"} ${p}`}
                        {isCurrent && <Check size={14} />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <ActionIconButton
            onClick={() => handleCloneElement(preview.cloneName, preview.fieldName)}
            tone="gray"
            className="!h-8 !w-8 !rounded-lg !border !border-slate-200 !bg-white shadow-sm hover:!bg-gray-50"
            ariaLabel={translations[langMode].Clone}
            tooltipText={translations[langMode].Clone}
            tooltipPosition="top"
          >
            <FaRegClone size={14} />
          </ActionIconButton>
          <ActionIconButton
            onClick={() => {
              console.log("Edit field clicked", preview);
              setOpen(true);
            }}
            tone="blue"
            className="!h-8 !w-8 !rounded-lg !border !border-slate-200 !bg-white shadow-sm hover:!bg-blue-50"
            ariaLabel={translations[langMode].updatefield}
            tooltipText={translations[langMode].updatefield}
            tooltipPosition="top"
          >
            <Pencil size={14} />
          </ActionIconButton>
          <ActionIconButton
            onClick={() => handleRemoveElement(preview.uniqueKey, preview.id)}
            tone="red"
            className="!h-8 !w-8 !rounded-lg !border !border-slate-200 !bg-white shadow-sm hover:!bg-red-50"
            ariaLabel={translations[langMode].deletefield}
            tooltipText={translations[langMode].deletefield}
            tooltipPosition="top"
          >
            <Trash2 size={14} />
          </ActionIconButton>
        </div>
      </div>
      <div key={preview.uniqueKey} className="mb-1 space-y-3">
        <Section title={translations[langMode].fieldname ?? "Field Name"}>
          <label htmlFor={`input-${preview.uniqueKey}`} className="sr-only">
            {preview.fieldName}
          </label>
          <input
            type="text"
            id={`input-${preview.uniqueKey}`}
            defaultValue={preview.fieldName || "Default Text"}
            className={`block w-full rounded-lg border border-slate-200 bg-white p-2.5 text-[13px] text-slate-800
                           focus:border-[#236DB4] focus:ring-[#EAF3FB]
                             dark:focus:ring-blue-500 dark:focus:border-blue-500  ${theme === "dark"
                ? "dark-mode "
                : theme === "high-contrast"
                  ? "high-contrast"
                  : ""
              }`}
            onChange={(event) => handleInputChange(event, preview.uniqueKey)}
          />
        </Section>

        <Section title={translations[langMode].validation ?? "Validation"}>
          <ValidationPreview
            setCurrentValidation={setCurrentValidation}
            setValidationModal={setValidationModal}
            setValidationId={setValidationId}
            previewKey={preview.uniqueKey}
            preview={preview}
            validationId={preview.id}
            validationFormValues={validationFormValues}
            setValidationFormValues={setValidationFormValues}
            setViewValidationModal={setViewValidationModal}
            viewValidationModal={viewValidationModal}
            droppedItems={droppedItems}
            setCompareValidationModal={setCompareValidationModal}
          />
        </Section>

        {(preview.fieldType === "radio" ||
          preview.fieldType === "file" ||
          preview.fieldType === "multi_image" ||
          preview.fieldType === "ocr") && (
          <Section
            title={
              translations[langMode].otherFieldSettings ??
              "Other Field Settings"
            }
          >
        {preview.fieldType === "radio" && (
          <div className="px-1 py-1">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold whitespace-nowrap">
                {translations[langMode].showAsFilter}
              </label>
              <input
                checked={!!preview.isOptionShow}
                type="checkbox"
                onChange={(event) =>
                  handleOptionShowToggle(event, preview.uniqueKey)
                }
                className="w-4 h-4 rounded border-gray-500 bg-white text-green-600 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-green-600 dark:ring-offset-gray-800"
              />
            </div>
          </div>
        )}
        {(preview.fieldType === "file" ||
          preview.fieldType === "multi_image" ||
          preview.fieldType === "ocr") && (
          <div className="px-1 py-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <label className="text-xs font-semibold whitespace-nowrap">
                  {translations[langMode].allowGallery ?? "Allow Gallery"}
                </label>
                <FieldInfoIcon
                  className="field-info-icon--wrap"
                  tooltipText={
                    translations[langMode].allowGalleryTooltip ??
                    "Enable the Allow Gallery option for this field."
                  }
                />
              </div>
              <input
                // Default true: only an explicit false turns the toggle off.
                checked={preview.allowGallery !== false}
                type="checkbox"
                onChange={(event) =>
                  handleAllowGalleryToggle(event, preview.uniqueKey)
                }
                className="w-4 h-4 rounded border-gray-500 bg-white text-green-600 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-green-600 dark:ring-offset-gray-800"
              />
            </div>
          </div>
        )}
          </Section>
        )}
        {(preview.fieldType === "file" ||
          preview.fieldType === "multi_image") && (
          <CollapsibleSection
            title={translations[langMode].photoDimensions ?? "Photo Dimensions"}
            defaultOpen={Boolean(
              preview.photoConstraints?.widthPx ||
                preview.photoConstraints?.heightPx ||
                preview.photoConstraints?.aspectRatio
            )}
          >
            <div className="mb-2 flex items-center gap-1">
              <FieldInfoIcon
                className="field-info-icon--wrap"
                tooltipText={
                  translations[langMode].photoDimensionsTooltip ??
                  "Optionally require captured photos to match a width, height and/or aspect ratio."
                }
              />
            </div>
            <div className="mt-2">
              <label className="block text-[11px] text-gray-600 mb-1">
                {translations[langMode].aspectRatio ?? "Aspect Ratio (optional)"}
              </label>
              <div className="text-xs">
                <ReactSelector
                  options={aspectRatios.map((ratio) => ({
                    value: ratio,
                    label:
                      ratio === "Custom"
                        ? translations[langMode].customAspectRatio ?? "Custom"
                        : ratio,
                  }))}
                  value={
                    preview.photoConstraints?.aspectRatio
                      ? {
                          value: preview.photoConstraints.aspectRatio,
                          label:
                            preview.photoConstraints.aspectRatio === "Custom"
                              ? translations[langMode].customAspectRatio ??
                                "Custom"
                              : preview.photoConstraints.aspectRatio,
                        }
                      : null
                  }
                  onChange={(selected) =>
                    handlePhotoConstraintChange(
                      preview.uniqueKey,
                      "aspectRatio",
                      selected?.value ?? "",
                    )
                  }
                  placeholder={
                    translations[langMode].aspectRatioSelect ??
                    "Select aspect ratio"
                  }
                  isSearchable={false}
                  isClearable={true}
                  height="38px"
                  borderRadius="6px"
                  fontSize="13px"
                />
              </div>
            </div>
            <div className="mt-2 flex gap-2">
              <div className="flex-1">
                <label className="block text-[11px] text-gray-600 mb-1">
                  {translations[langMode].widthPx ?? "Width (px)"}
                </label>
                <input
                  type="number"
                  min="1"
                  placeholder="e.g. 1080"
                  value={preview.photoConstraints?.widthPx ?? ""}
                  onChange={(e) =>
                    handlePhotoConstraintChange(
                      preview.uniqueKey,
                      "widthPx",
                      e.target.value,
                    )
                  }
                  className={`block w-full rounded-lg border border-slate-200 bg-white p-2 text-sm text-slate-800 focus:border-[#236DB4] focus:ring-[#EAF3FB] ${
                    theme === "dark"
                      ? "dark-mode "
                      : theme === "high-contrast"
                        ? "high-contrast"
                        : ""
                  }`}
                />
              </div>
              <div className="flex-1">
                <label className="block text-[11px] text-gray-600 mb-1">
                  {translations[langMode].heightPx ?? "Height (px)"}
                </label>
                <input
                  type="number"
                  min="1"
                  placeholder="e.g. 1350"
                  value={preview.photoConstraints?.heightPx ?? ""}
                  onChange={(e) =>
                    handlePhotoConstraintChange(
                      preview.uniqueKey,
                      "heightPx",
                      e.target.value,
                    )
                  }
                  className={`block w-full rounded-lg border border-slate-200 bg-white p-2 text-sm text-slate-800 focus:border-[#236DB4] focus:ring-[#EAF3FB] ${
                    theme === "dark"
                      ? "dark-mode "
                      : theme === "high-contrast"
                        ? "high-contrast"
                        : ""
                  }`}
                />
              </div>
            </div>
            {(preview.photoConstraints?.widthPx ||
              preview.photoConstraints?.heightPx ||
              preview.photoConstraints?.aspectRatio) && (
              <p className="mt-2 text-[11px] font-medium text-blue-600">
                {translations[langMode].configuredDimensions ??
                  "Configured dimensions"}
                :{" "}
                {preview.photoConstraints?.widthPx ?? "-"} ×{" "}
                {preview.photoConstraints?.heightPx ?? "-"} px
                {preview.photoConstraints?.aspectRatio
                  ? ` (${
                      preview.photoConstraints.aspectRatio === "Custom"
                        ? translations[langMode].customAspectRatio ?? "Custom"
                        : preview.photoConstraints.aspectRatio
                    })`
                  : ""}
              </p>
            )}
          </CollapsibleSection>
        )}

        <Section title={Translations[langMode].mandatory ?? "Mandatory"}>
          <div className="flex items-center gap-2">
            <input
              fieldName={`toggle-${preview.uniqueKey}`}
              checked={preview.isMandatory}
              id={`green-checkbox toggle-${preview.uniqueKey}`}
              type="checkbox"
              onChange={(event) => handleToggleChange(event, preview.uniqueKey)}
              className="h-4 w-4 rounded border-gray-500 bg-white text-green-600 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-green-600 dark:ring-offset-gray-800"
            />
            <label className="text-xs font-semibold whitespace-nowrap">
              {translations[langMode].requiredField ?? "Field is required"}
            </label>
          </div>
        </Section>

        <Section
          title={Translations[langMode].excel ?? "Excel Column Name"}
          right={<span className="text-xs text-red-500">*</span>}
        >
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder={
                isEdit || isClone ? "Enter Excel Name.." : "Enter Excel Name..."
              }
              className={`block w-full rounded-lg border border-slate-200 bg-white p-2.5 text-sm text-slate-800
                                  transition duration-200 ease-in-out focus:border-[#236DB4] focus:ring-[#EAF3FB]
                                   dark:focus:ring-blue-500 dark:focus:border-blue-500  ${theme === "dark"
                  ? "dark-mode "
                  : theme === "high-contrast"
                    ? "high-contrast"
                    : ""
                }`}
              value={
                isEdit || isClone
                  ? pdfInput !== undefined
                    ? pdfInput
                    : preview.excelReportName
                  : pdfInput
              }
              onChange={handlePdfInput}
            />
            <GiCheckMark
              onClick={() =>
                isEdit || isClone
                  ? addPdffield(preview.uniqueKey)
                  : addPdffield(preview.uniqueKey, pdfInput)
              }
              className={`cursor-pointer transition duration-300 ease-in-out transform hover:scale-110 ${(
                  isEdit || isClone ? pdfInput || preview.excelReportName : pdficon
                )
                  ? "text-green-500 hover:text-green-700"
                  : "text-red-500 hover:text-red-700"
                }`}
              size={20}
            />
          </div>
        </Section>

        <Section title={Translations[langMode].PDF ?? "PDF / Report"}>
          <div className="flex items-center gap-2">
            <input
              fieldName={`toggle-${preview.uniqueKey}`}
              checked={reportField}
              id={`green-checkbox toggle-${preview.uniqueKey}`}
              type="checkbox"
              onChange={(event) => reportMandatory(event, preview.uniqueKey)}
              className="h-4 w-4 rounded border-gray-500 bg-white text-green-600 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-green-600 dark:ring-offset-gray-800"
            />
            <label className="text-xs font-semibold whitespace-nowrap">
              {Translations[langMode].PDF}
            </label>
          </div>
          {reportField && (
            <div className="mt-3 flex items-center gap-2">
              <input
                type="text"
                placeholder={translations[langMode]?.wfEnterPdfName ?? "Enter PDF Name..."}
                className={`block w-full rounded-lg border border-slate-200 bg-white p-2.5 text-sm text-slate-800
                                   transition duration-200 ease-in-out focus:border-[#236DB4] focus:ring-[#EAF3FB] dark:focus:ring-blue-500
                                    dark:focus:border-blue-500  ${theme === "dark"
                    ? "dark-mode "
                    : theme === "high-contrast"
                      ? "high-contrast"
                      : ""
                  }`}
                value={
                  isEdit || isClone
                    ? reportInput !== undefined
                      ? reportInput
                      : preview.reportName
                    : reportInput
                }
                onChange={handleReportInput}
              />
              <GiCheckMark
                onClick={() =>
                  isEdit || isClone
                    ? addReportfield(preview.uniqueKey)
                    : addReportfield(preview.uniqueKey, reportInput)
                }
                className={`cursor-pointer transition duration-300 ease-in-out transform hover:scale-110 ${(
                    isEdit || isClone ? reportInput || preview.reportName : reporticon
                  )
                    ? "text-green-500 hover:text-green-700"
                    : "text-red-500 hover:text-red-700"
                  }`}
                size={20}
              />
            </div>
          )}
        </Section>

        {isNewFieldDragged &&
          (selectedParentOptionRows.length > 0 || displaySelectedValue) && (
            <Section title={`${Translations[langMode].selectedOption}`}>
              {selectedParentOptionRows.length > 0 ? (
                selectedParentOptionRows.map((row) => (
                  <div key={row.key} className="mt-3 first:mt-0">
                    <span className="block text-[13px] font-semibold leading-[1.45]">
                      {Translations[langMode].Que}: {row.que}
                    </span>
                    <span className="block text-[13px] font-semibold leading-[1.45]">
                      {Translations[langMode].option}: {row.option}
                    </span>
                  </div>
                ))
              ) : displaySelectedValue ? (
                <>
                  <span className="block text-[13px] font-semibold leading-[1.45]">
                    {Translations[langMode].Que}: {question}
                  </span>
                  <span className="block text-[13px] font-semibold leading-[1.45]">
                    {Translations[langMode].option}: {displaySelectedValue}
                  </span>
                </>
              ) : null}
            </Section>
          )}
        {shouldRenderSelectOption && (
          <CollapsibleSection
            title={
              Translations[langMode].visibilityRules ??
              Translations[langMode].selectoption
            }
            defaultOpen={Boolean(preview.visibilityRules?.length)}
          >
            <div className="overflow-y-auto max-h-[400px] h-auto px-2">
              {visibilityRuleGroups.map((_, ruleIndex) => (
                <div
                  key={`rule-${ruleIndex}`}
                  className="mb-3 last:mb-0 rounded-lg border border-dashed border-slate-300 bg-white p-2"
                >
                  <p className="mb-2 text-[12px] font-semibold leading-[1.45] text-slate-500">
                    {visibilityRuleGroups.length > 1
                      ? `Rule ${ruleIndex + 1} (all conditions must match)`
                      : "Link parent options (all checked conditions must match)"}
                  </p>
                  {selectableVisibilityGroups.map(([uniqueKey, groupInputs], groupIndex) => (
                    <div
                      key={`${ruleIndex}-${uniqueKey}`}
                      className={`rounded-md p-2 mt-2 ${groupIndex === selectableVisibilityGroups.length - 1 ? "mb-0" : "mb-2"} ${shouldRenderSelectOptionEdit &&
                        uniqueKey === preview.uniqueKey
                        ? ""
                        : "border border-slate-300"
                        }`}
                    >
                      <h3
                        className="mb-2 flex cursor-pointer items-center justify-between text-[12px] font-semibold"
                        onClick={() => toggleCollapse(uniqueKey)}
                      >
                        {uniqueKey !== preview.uniqueKey
                          ? getHeadline(uniqueKey)
                          : ""}
                        {!(
                          shouldRenderSelectOptionEdit &&
                          uniqueKey === preview.uniqueKey
                        ) && (
                            <span className="ml-2">
                              {collapsedGroups[uniqueKey] ? (
                                <FaChevronDown className="h-3" />
                              ) : (
                                <FaChevronUp className="h-3" />
                              )}
                            </span>
                          )}
                      </h3>

                      <div
                        className={`transition-max-height transition-all ease-in-out duration-300 overflow-hidden ${collapsedGroups[uniqueKey] ? "max-h-0" : "max-h-screen"
                          }`}
                      >
                        {groupInputs.map((input) => {
                          const inRule = preview.visibilityRules?.[
                            ruleIndex
                          ]?.conditions?.some(
                            (c) =>
                              c.sourceUniqueKey === input.uniqueKey &&
                              c.expectedValue === input.value
                          );
                          const legacyRow =
                            ruleIndex === 0 &&
                            !preview.visibilityRules?.length &&
                            preview.radioId === optionInputKeyFor(input);
                          const isChecked = inRule || legacyRow;

                          return input.uniqueKey !== preview.uniqueKey ? (
                            <div
                              key={`${ruleIndex}-${input.id}-${input.uniqueKey}`}
                              className="flex items-center gap-3 px-3 mt-2.5"
                            >
                              <input
                                type="text"
                                value={input.value}
                                className={`block w-full rounded-lg border border-slate-200 bg-white p-2.5 text-sm text-slate-800 focus:border-[#236DB4] focus:ring-[#EAF3FB]
                     dark:focus:ring-blue-500 dark:focus:border-blue-500 ${theme === "dark"
                                    ? "dark-mode"
                                    : theme === "high-contrast"
                                      ? "high-contrast"
                                      : ""
                                  }`}
                                readOnly
                              />
                              <label className="flex items-center gap-1 text-xs text-gray-600">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  name={`vis-${preview.uniqueKey}-${ruleIndex}-${input.uniqueKey}-${input.id}`}
                                  onChange={(e) => {
                                    handleRadioBtnBelongs(
                                      input.uniqueKey,
                                      input.id,
                                      input.value,
                                      preview,
                                      input.radioKey,
                                      ruleIndex,
                                      e.target.checked
                                    );
                                    setIsChecked(true);
                                  }}
                                  className="h-4 w-4 rounded border-gray-500 text-blue-600"
                                />
                              </label>
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {canLinkWorkflow && (
          <CollapsibleSection
            title={translations[langMode].linkedWorkflow ?? "Linked Workflow"}
            defaultOpen={linkedWorkflowCount > 0}
            badge={
              linkedWorkflowCount > 0 ? (
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold normal-case tracking-normal text-[#236DB4]">
                  {linkedWorkflowCount}
                </span>
              ) : null
            }
          >
            <div className="mb-2 flex items-center gap-1">
              <FieldInfoIcon
                className="field-info-icon--wrap"
                tooltipText={
                  translations[langMode].linkedWorkflowTooltip ??
                  "Pick the workflow to link to each option. The link only affects report / PDF output."
                }
              />
            </div>
            {linkableOptions.length === 0 ? (
              <p className="text-[11px] font-medium text-slate-400">
                {translations[langMode].linkedWorkflowNoOptions ??
                  "Add at least one option to link a workflow."}
              </p>
            ) : linkedWorkflowsLoading ? (
              <p className="text-[11px] font-medium text-slate-400">
                {translations[langMode].linkedWorkflowLoading ??
                  "Loading workflows..."}
              </p>
            ) : linkedWorkflowOptions.length === 0 ? (
              <p className="text-[11px] font-medium text-slate-400">
                {translations[langMode].linkedWorkflowNoWorkflows ??
                  "No other active workflow is available in this project."}
              </p>
            ) : (
              <div className="max-h-[400px] space-y-3 overflow-y-auto">
                {linkableOptions.map((input) => {
                  const linkedId = getLinkedWorkflowIdForOption(
                    preview,
                    input.inputValue
                  );
                  const match = linkedWorkflowOptions.find(
                    (option) => Number(option.value) === Number(linkedId)
                  );
                  // A rule can outlive its target (deleted workflow, or a link
                  // made before the workflow left the project). Showing the raw
                  // id keeps the clear button reachable -- otherwise the option
                  // looks unset while still blocking the save.
                  const selected =
                    match ??
                    (linkedId != null
                      ? { value: linkedId, label: `Workflow ${linkedId}` }
                      : null);
                  return (
                    <div key={input.inputKey}>
                      <label className="mb-1 block text-[11px] text-gray-600">
                        {input.inputValue}
                      </label>
                      <div className="text-xs">
                        <ReactSelector
                          options={linkedWorkflowOptions}
                          value={selected}
                          onChange={(option) =>
                            onLinkedWorkflowChange?.(
                              preview.uniqueKey,
                              input.inputValue,
                              option?.value ?? null
                            )
                          }
                          placeholder={
                            translations[langMode].linkedWorkflowSelect ??
                            "Select workflow"
                          }
                          isClearable={true}
                          height="38px"
                          borderRadius="6px"
                          fontSize="13px"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {advancedLinkedRules.length > 0 && (
              <div className="mt-3 rounded-md border border-dashed border-slate-300 p-2">
                <p className="mb-1 text-[11px] font-semibold text-slate-500">
                  {translations[langMode].linkedWorkflowAdvancedRules ??
                    "Rules set outside this panel (kept on save)"}
                </p>
                {advancedLinkedRules.map((rule, index) => (
                  <p
                    key={`advanced-linked-rule-${index}`}
                    className="text-[11px] text-slate-600"
                  >
                    {formatLinkedWorkflowRuleSummary(
                      droppedItems,
                      rule,
                      linkedWorkflowNameById
                    )}
                  </p>
                ))}
              </div>
            )}
          </CollapsibleSection>
        )}

        {shouldRenderSelectOptionEdit && (
          <CollapsibleSection
            title={Translations[langMode].editInputvalue}
            defaultOpen={isEditShow}
          >
            <div className="pb-2">
              <button
                onClick={() => setIsEditShow((val) => !val)}
                className="inline-flex items-center rounded-lg bg-[#236DB4] px-3 py-2 text-center text-xs font-medium text-white transition-colors hover:bg-[#1B5690] focus:outline-none focus:ring-2 focus:ring-[#EAF3FB]"
              >
                {Translations[langMode].editInputvalue}
              </button>
            </div>
            {isEditShow && (
              <>
                {radioButtons.map((input) =>
                  input.uniqueKey === preview.uniqueKey &&
                    input.value !== "" ? (
                    <div
                      key={input.id}
                      className="flex flex-col lg:flex-row items-center space-x-2.5 mt-2.5"
                    >
                      <input
                        type="text"

                        value={
                          tempEditValues.hasOwnProperty(`${input.uniqueKey}-${input.id}`)
                            ? tempEditValues[`${input.uniqueKey}-${input.id}`]
                            : input.value
                        }

                        onChange={(e) => {
                          setTempEditValues((prevValues) => ({
                            ...prevValues,
                            [`${input.uniqueKey}-${input.id}`]: e.target.value, // Allow empty string
                          }));
                        }}

                        className={`block w-full rounded-lg border border-slate-200 bg-white p-2.5 text-sm text-slate-800
                                      focus:border-[#236DB4] focus:ring-[#EAF3FB]
                                        dark:focus:ring-blue-500 dark:focus:border-blue-500  ${theme === "dark"
                            ? "dark-mode "
                            : theme === "high-contrast"
                              ? "high-contrast"
                              : ""
                          }`}
                      />
                      <>
                        <MdCheck
                          onClick={() => {
                            const editValue =
                              tempEditValues[
                              `${input.uniqueKey}-${input.id}`
                              ] || input.value;
                            handleRadioOptions(
                              input.id,
                              editValue,
                              preview.uniqueKey
                            );
                          }}
                        />
                        <Trash2
                          size={18}
                          className="cursor-pointer text-red-500 hover:text-red-600"
                          onClick={() => {
                            const editValue =
                              tempEditValues[
                                `${input.uniqueKey}-${input.id}`
                              ] || input.value;
                            handleRadioDeleteOptions(
                              input.id,
                              input.radioKey,
                              editValue,
                              preview.uniqueKey
                            );
                          }}
                        />
                      </>
                    </div>
                  ) : null
                )}
              </>
            )}
          </CollapsibleSection>
        )}

        {(preview.fieldType === "select" ||
          preview.fieldType === "radio" ||
          preview.fieldType === "checkbox") && (
          <CollapsibleSection
            title={Translations[langMode].addremove}
            defaultOpen
            badge={
              <span className="flex items-center gap-2 normal-case tracking-normal text-slate-500">
                <FaPlus
                  size={12}
                  className="hover:text-[#236DB4]"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleAddInput(preview.uniqueKey, preview.page);
                  }}
                />
                <FaMinus
                  size={12}
                  className="hover:text-red-600"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleRemoveInput(event);
                  }}
                />
              </span>
            }
          >
                {inputValues.map((input) =>
                  input.uniqueKey === preview.uniqueKey ? (
                    <div
                      key={input.id}
                      className="flex items-center space-x-2.5 mt-2.5"
                    >
                      <input
                        type="text"
                        value={
                          tempInputValue[`${input.uniqueKey}-${input.id}`] ||
                          input.value
                        }
                        onChange={(e) =>
                          setTempInputValue((prevValues) => ({
                            ...prevValues,
                            [`${input.uniqueKey}-${input.id}`]: e.target.value,
                          }))
                        }
                        className="block w-full rounded-lg border border-slate-200 bg-white p-2.5 text-sm text-slate-800 focus:border-[#236DB4] focus:ring-[#EAF3FB] dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      />
                      <GiCheckMark
                        onClick={() => {
                          const editValue =
                            tempInputValue[`${input.uniqueKey}-${input.id}`] ||
                            input.value;

                          if (!editValue.trim()) {
                            toast.error(translations[langMode].inputfield);
                            return;
                          }
                          handleRadioOptions(
                            input.id,
                            editValue,
                            preview.uniqueKey
                          );
                        }}
                      />
                    </div>
                  ) : null
                )}
          </CollapsibleSection>
        )}
      </div>
      <ViewValidationModal
        previewKey={preview.uniqueKey}
        preview={preview}
        setViewValidationModal={setViewValidationModal}
        viewValidationModal={viewValidationModal}
        currentValidation={currentValidation}
        setCurrentValidation={setCurrentValidation}
        validationId={validationId}
        setValidationFormValues={setValidationFormValues}
        validationFormValues={validationFormValues}
        updateValidationField={updateValidationField}
        handleValidationDelete={handleValidationDelete}
        droppedItems={droppedItems}
      />
      <FieldModal open={open} setOpen={setOpen} preview={preview} setPreview={setPreview} setDroppedItems={setDroppedItems} setDragItems={setDragItems}
        dragItems={dragItems}
        droppedItems={droppedItems} />
    </div>
  );
};

export default FieldDetails;
